import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Paper, Container, Backdrop, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchForm from '../components/SearchForm';
import AnalysisProgressTracker from '../components/AnalysisProgressTracker';
import Onboarding from '../components/Onboarding';
import { submitAnalysisTask, getTaskStatus, getTaskResult, cancelTask } from '../services/api';
import { useCompany } from '../contexts/CompanyContext';

const HomePage = () => {
  const navigate = useNavigate();
  const { updateCompanies } = useCompany();

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [taskProgress, setTaskProgress] = useState(null);
  const [activeTaskId, setActiveTaskId] = useState(null);

  const pollingIntervalRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const handleTaskComplete = useCallback((taskId, result) => {
    let companies = null;
    if (result.results && Array.isArray(result.results)) {
      companies = result.results;
    } else if (result.companies && Array.isArray(result.companies)) {
      companies = result.companies;
    } else if (Array.isArray(result)) {
      companies = result;
    }

    const city = result.city || localStorage.getItem('search_city') || 'Результаты поиска';
    const total = companies ? companies.length : 0;
    const companiesWithAnalysis = companies ? companies.filter(c => c.analysis || c.gemini_analysis).length : 0;
    const successfulAnalysis = companies ? companies.filter(c => {
      const analysis = c.analysis || c.gemini_analysis;
      return analysis && analysis.categories && analysis.categories.length > 0;
    }).length : 0;

    updateCompanies(companies || [], city, {
      total,
      processing_time_ms: result.processing_time_ms || 0,
      analysis_statistics: {
        total,
        analyzed: companiesWithAnalysis,
        successful: successfulAnalysis,
        success_rate: total > 0 ? (successfulAnalysis / total) * 100 : 0
      },
      task_id: taskId
    });

    stopPolling();
    localStorage.removeItem('current_task_id');
    navigate(`/results/${taskId}`);
    setIsAnalyzing(false);
    setLoading(false);
    setActiveTaskId(null);
    setTaskProgress(null);
  }, [navigate, stopPolling, updateCompanies]);

  const pollTaskStatus = useCallback(async (taskId) => {
    try {
      const status = await getTaskStatus(taskId);

      if (status && status.status) {
        const taskStatus = status.status.toLowerCase();

        if (taskStatus === 'failed' || taskStatus === 'failure') {
          stopPolling();
          localStorage.removeItem('current_task_id');
          setIsAnalyzing(false);
          setLoading(false);
          setActiveTaskId(null);
          setTaskProgress(null);
          return;
        }

        if (taskStatus === 'completed' || taskStatus === 'success') {
          const result = await getTaskResult(taskId);
          if (result && (result.results || result.companies || Array.isArray(result))) {
            handleTaskComplete(taskId, result);
            return;
          }
        }

        setTaskProgress(prev => prev ? {
          status: taskStatus,
          message: status.message || prev.message || 'Обработка...'
        } : prev);
      }
    } catch (err) {
      console.error('Poll error:', err);
    }
  }, [handleTaskComplete, stopPolling]);

  // Восстановление задачи при загрузке страницы (рефреш / новая вкладка)
  useEffect(() => {
    const savedTaskId = localStorage.getItem('current_task_id');
    if (!savedTaskId) return;

    const checkOnce = async () => {
      try {
        const status = await getTaskStatus(savedTaskId);

        // Если задача провалилась — чистим
        if (status && (status.status === 'failed' || status.status === 'failure')) {
          localStorage.removeItem('current_task_id');
          return;
        }

        // Если задача уже завершена — получаем результат и редиректим
        if (status && (status.status === 'completed' || status.status === 'success')) {
          const result = await getTaskResult(savedTaskId);
          if (result && (result.results || result.companies || Array.isArray(result))) {
            handleTaskComplete(savedTaskId, result);
            return;
          }
        }

        // Задача ещё идёт — показываем модалку и запускаем поллинг
        setTaskProgress({ status: status?.status || 'in_progress', message: status?.message || 'Обработка...' });
        setIsAnalyzing(true);
        setActiveTaskId(savedTaskId);
      } catch {
        // При ошибке запускаем поллинг с модалкой
        setTaskProgress({ status: 'in_progress', message: 'Обработка...' });
        setIsAnalyzing(true);
        setActiveTaskId(savedTaskId);
      }
    };

    checkOnce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Поллинг
  useEffect(() => {
    stopPolling();

    if (!activeTaskId) return;

    pollTaskStatus(activeTaskId);

    const interval = setInterval(() => {
      pollTaskStatus(activeTaskId);
    }, 3000);

    pollingIntervalRef.current = interval;

    return () => stopPolling();
  }, [activeTaskId, pollTaskStatus, stopPolling]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && activeTaskId) {
        pollTaskStatus(activeTaskId);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [activeTaskId, pollTaskStatus]);

  const handleSearchResults = useCallback((results) => {
    let taskId = results.task_id || localStorage.getItem('current_task_id');

    if (results.task_id) {
      const oldTaskId = localStorage.getItem('current_task_id');
      if (oldTaskId && oldTaskId !== results.task_id) stopPolling();
      localStorage.setItem('current_task_id', results.task_id);
      taskId = results.task_id;
    }

    if (taskId) setActiveTaskId(taskId);

    setTaskProgress({ status: 'in_progress', message: 'Инициализация анализа...' });
    setIsAnalyzing(true);
    setLoading(false);
  }, [stopPolling]);

  const handleSearchStart = () => {
    setLoading(true);
    setIsAnalyzing(false);
    setTaskProgress(null);
  };

  const handleSearchError = (errorMessage) => {
    if (errorMessage.includes('timeout') || errorMessage.includes('время')) return;
    console.error('Search error:', errorMessage);
    setLoading(false);
    setIsAnalyzing(false);
    localStorage.removeItem('current_task_id');
    setActiveTaskId(null);
  };

  const handleCreateAsyncTask = useCallback(async (city, orgCount) => {
    try {
      stopPolling();
      setLoading(true);
      setIsAnalyzing(true);

      const taskResponse = await submitAnalysisTask(city, orgCount);

      if (taskResponse.task_id) {
        setActiveTaskId(taskResponse.task_id);
        localStorage.setItem('current_task_id', taskResponse.task_id);
        setTaskProgress({ status: 'in_progress', message: 'Задача отправлена на обработку...' });
      }
    } catch (err) {
      console.error('Error creating task:', err);
      setLoading(false);
      setIsAnalyzing(false);
      localStorage.removeItem('current_task_id');
      setActiveTaskId(null);
    }
  }, [stopPolling]);

  const handleCancelSearch = useCallback(async () => {
    stopPolling();
    if (activeTaskId) {
      try {
        await cancelTask(activeTaskId);
      } catch (err) {
        console.error('Failed to cancel task:', err);
      }
    }
    localStorage.removeItem('current_task_id');
    setIsAnalyzing(false);
    setLoading(false);
    setActiveTaskId(null);
    setTaskProgress(null);
  }, [stopPolling, activeTaskId]);

  const searchDisabled = isAnalyzing || loading || !!activeTaskId;

  return (
    <Container maxWidth="lg">
      <Onboarding />

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 3,
          backgroundColor: 'rgba(0, 0, 0, 0.8)'
        }}
        open={loading && !isAnalyzing}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h5">Поиск и анализ компаний...</Typography>
      </Backdrop>

      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Система анализа компаний
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Поиск и анализ компаний с помощью искусственного интеллекта
        </Typography>
      </Box>

      <SearchForm
        onSearchResults={handleSearchResults}
        onSearchStart={handleSearchStart}
        onSearchError={handleSearchError}
        onCreateAsyncTask={handleCreateAsyncTask}
        disabled={searchDisabled}
      />

      {isAnalyzing && taskProgress && (
        <AnalysisProgressTracker
          taskProgress={taskProgress}
          isAnalyzing={isAnalyzing}
          onCancel={handleCancelSearch}
        />
      )}

      {!isAnalyzing && !loading && !activeTaskId && (
        <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.default', mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Как работает наша система
          </Typography>
          <Typography variant="body2" paragraph>
            Наша система автоматически находит компании в любом городе и анализирует их с помощью искусственного интеллекта для получения подробной информации.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ flex: 1, p: 2, bgcolor: 'primary.light', borderRadius: 2, color: 'primary.contrastText' }}>
              <Typography variant="subtitle2" gutterBottom>1. Автоматический поиск</Typography>
              <Typography variant="body2">
                Поиск компаний в различных базах данных: реестрах, картах, официальных источниках.
              </Typography>
            </Box>
            <Box sx={{ flex: 1, p: 2, bgcolor: 'secondary.light', borderRadius: 2, color: 'secondary.contrastText' }}>
              <Typography variant="subtitle2" gutterBottom>2. Умный анализ</Typography>
              <Typography variant="body2">
                Искусственный интеллект анализирует найденную информацию и делает выводы о каждой компании.
              </Typography>
            </Box>
            <Box sx={{ flex: 1, p: 2, bgcolor: 'success.light', borderRadius: 2, color: 'success.contrastText' }}>
              <Typography variant="subtitle2" gutterBottom>3. Подробные результаты</Typography>
              <Typography variant="body2">
                Получите детальную информацию, оценки и рекомендации по каждой найденной компании.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
            <Typography variant="subtitle2" gutterBottom color="info.contrastText">
              Совет: Для получения наиболее полной информации рекомендуется анализировать 10 и более компаний
            </Typography>
            <Typography variant="body2" color="info.contrastText">
              Система работает быстрее при обработке нескольких компаний одновременно и предоставляет более качественные результаты.
            </Typography>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default HomePage;
